/**
 * Error Boundary for Admin Portal
 *
 * Catches errors in the admin portal and displays a user-friendly error page.
 * Allows the user to reload or navigate back to the main app.
 * Post-deploy missing hashed chunks auto-reload once (same helper as the root SPA boundary).
 */

import React, { Component, type ReactNode } from 'react';
import { Container, Title, Text, Button, Group, Stack, Alert } from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconHome } from '@tabler/icons-react';
import { APP_ROUTES } from '../../constants/routes';
import { brandColors, semanticColors } from '../../config/theme';
import {
  reloadForStaleDeployChunk,
  shouldAutoReloadForStaleDeployChunk,
} from '../../utils/staleDeployChunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** True while recovering from a post-deploy missing chunk. */
  autoReloadingStaleChunk: boolean;
}

/**
 * Error boundary component for catching and displaying errors in the admin portal.
 * Class component because React error boundaries require getDerivedStateFromError.
 */
export class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, autoReloadingStaleChunk: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      autoReloadingStaleChunk: shouldAutoReloadForStaleDeployChunk(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console - in production, this would go to a logging service
    console.error('[Admin Error Boundary] Caught error:', error, errorInfo);

    if (this.state.autoReloadingStaleChunk) {
      console.warn('[Admin Error Boundary] Stale deploy chunk — reloading for new assets');
      reloadForStaleDeployChunk();
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = APP_ROUTES.overview;
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.state.autoReloadingStaleChunk) {
        return (
          <Container size="sm" py="xl">
            <Stack align="center" gap="lg">
              <Title order={2} ta="center">
                A new version is available. Reloading…
              </Title>
            </Stack>
          </Container>
        );
      }

      return (
        <Container size="sm" py="xl">
          <Stack align="center" gap="lg">
            <IconAlertTriangle size={64} color="var(--mantine-color-red-6)" />

            <Title order={2} ta="center">
              Something went wrong
            </Title>

            <Text c={semanticColors.text.secondary} ta="center">
              An error occurred while loading this admin page.
              Please try again or return to the main application.
            </Text>

            {this.state.error && (
              <Alert color={brandColors.danger} title="Error Details" w="100%">
                <Text size="sm" style={{ fontFamily: 'monospace' }}>
                  {this.state.error.message}
                </Text>
              </Alert>
            )}

            <Group>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={this.handleRetry}
              >
                Try Again
              </Button>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
              <Button
                leftSection={<IconHome size={16} />}
                onClick={this.handleGoHome}
              >
                Go to Overview
              </Button>
            </Group>
          </Stack>
        </Container>
      );
    }

    return this.props.children;
  }
}
